import * as SelectPrimitive from '@radix-ui/react-select';
import type { IconName } from 'lucide-react/dynamic';
import * as React from 'react';
import { Icon } from '@/components/ui/icon';
import { InputGroupInput } from '@/components/ui/input-group';
import { Select, SelectContent, SelectGroup, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { STATUS_VARIANTS } from './status';

/** Casca visual comum aos campos do wizard de pedido: label caixa-alta + área de conteúdo. */
export function WizardField({
  label,
  hint,
  required,
  className,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </span>
        {hint && <span className="text-[11px] text-faint">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

/** Casca visual comum a todo controle de 44px do wizard (input, select, trigger de async-select). */
export const wizardControlClass =
  'h-11 w-full rounded-[12px] border border-border bg-raised text-[13.5px] text-foreground outline-none transition-colors placeholder:text-faint hover:border-border-strong focus-visible:border-primary focus-visible:shadow-[0_0_0_4px_var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60';

interface WizardInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: IconName;
  mono?: boolean;
}

/**
 * Input de 44px no padrão do wizard, com ícone opcional à esquerda.
 * `forwardRef` é obrigatório aqui: `form.register()` passa um `ref` pra
 * amarrar o input não controlado do react-hook-form — sem isso o valor
 * inicial (defaultValues) nunca chega no DOM e o campo nasce vazio.
 */
export const WizardInput = React.forwardRef<HTMLInputElement, WizardInputProps>(
  ({ icon, mono, className, ...props }, ref) => (
    <div className="relative">
      {icon && (
        <span className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-muted-foreground">
          <Icon name={icon} size={16} />
        </span>
      )}
      <input
        ref={ref}
        className={cn(
          wizardControlClass,
          icon ? 'pl-10' : 'pl-3.5',
          'pr-3.5',
          mono && 'font-mono',
          className,
        )}
        {...props}
      />
    </div>
  ),
);
WizardInput.displayName = 'WizardInput';

interface WizardSelectOption {
  label: string;
  value: string;
}

interface WizardSelectProps {
  icon?: IconName;
  placeholder?: string;
  value?: string;
  onValueChange: (value: string) => void;
  options: WizardSelectOption[];
  disabled?: boolean;
  className?: string;
}

/** Select de 44px no padrão do wizard, com chevron próprio e ícone opcional à esquerda. */
export function WizardSelect({
  icon,
  placeholder = 'Selecionar',
  value,
  onValueChange,
  options,
  disabled,
  className,
}: WizardSelectProps) {
  return (
    <Select value={value ?? ''} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        className={cn(
          wizardControlClass,
          'relative flex items-center gap-2',
          icon ? 'pl-10' : 'pl-3.5',
          'pr-3.5',
          !value && 'text-faint',
          className,
        )}
      >
        {icon && (
          <span className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-muted-foreground">
            <Icon name={icon} size={16} />
          </span>
        )}
        <span className="flex-1 truncate text-left">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon asChild>
          <Icon
            name="chevron-down"
            size={16}
            className="shrink-0 text-muted-foreground"
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectContent>
        <SelectGroup>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

/** Chip clicável de 44px contendo um switch + rótulo — substitui `<Switch/><span>` solto. */
export function ToggleChip({
  checked,
  onCheckedChange,
  children,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label
      className={cn(
        'flex h-11 cursor-pointer items-center gap-2.5 rounded-[10px] border px-3.5 text-[13px] font-medium transition-colors',
        checked
          ? 'border-primary-line bg-primary-soft text-foreground'
          : 'border-border-strong bg-card text-foreground-2 hover:bg-muted',
      )}
    >
      <Switch checked={checked} onCheckedChange={onCheckedChange} className="shrink-0" />
      {children}
    </label>
  );
}

/** Trilho -/+ de 44px pra editar quantidade, com número centralizado em mono.
 * `editable` habilita digitar o valor direto, em vez de só usar os botões -/+. */
export function QuantityStepper({
  value,
  onChange,
  min = 1,
  editable = false,
}: {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  editable?: boolean;
}) {
  const [text, setText] = React.useState(String(value));

  React.useEffect(() => {
    setText(String(value));
  }, [value]);

  const commit = (raw: string) => {
    const parsed = Number(raw);

    if (raw === '' || Number.isNaN(parsed)) {
      setText(String(value));
      return;
    }

    const next = Math.max(min, parsed);
    onChange(next);
    setText(String(next));
  };

  return (
    <div className="flex h-11 items-center rounded-[12px] border border-border bg-raised">
      <button
        type="button"
        className="flex h-full w-11 shrink-0 items-center justify-center rounded-l-[12px] text-muted-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        <Icon name="minus" size={16} />
      </button>

      {editable ? (
        <InputGroupInput
          type="text"
          inputMode="numeric"
          className="text-center font-mono text-[14px] font-bold tabular-nums"
          value={text}
          onChange={(e) => {
            const raw = e.target.value;
            if (/^\d*$/.test(raw)) {
              setText(raw);
            }
          }}
          onBlur={() => commit(text)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit(text);
              e.currentTarget.blur();
            }
          }}
        />
      ) : (
        <span className="flex-1 text-center font-mono text-[14px] font-bold tabular-nums">
          {value}
        </span>
      )}

      <button
        type="button"
        className="flex h-full w-11 shrink-0 items-center justify-center rounded-r-[12px] text-muted-foreground transition-colors hover:bg-muted"
        onClick={() => onChange(value + 1)}
      >
        <Icon name="plus" size={16} />
      </button>
    </div>
  );
}

// Reaproveita os mesmos 4 "baldes" de cor do `StatusBadge` (secondary/default/
// success/destructive) pra colorir a pill ativa igual ao badge que aparece no
// cabeçalho e no kanban — sem inventar uma paleta nova pra status.
const STATUS_BUCKET_DOT: Record<string, string> = {
  secondary: 'bg-foreground-2',
  default: 'bg-primary',
  success: 'bg-success',
  destructive: 'bg-destructive',
  warning: 'bg-warning',
};

const STATUS_BUCKET_ACTIVE: Record<string, string> = {
  secondary: 'border-foreground-2 bg-foreground/10 text-foreground',
  default: 'border-primary bg-primary-soft text-primary',
  success: 'border-success bg-success-soft text-success',
  destructive: 'border-destructive bg-destructive-soft text-destructive',
  warning: 'border-warning bg-warning-soft text-warning',
};

/** Pills de status (progressão do pedido) — usado no lugar de um `<select>` no wizard. */
export function StatusPillSelect({
  value,
  onValueChange,
  options,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: { label: string; value: string }[];
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const active = option.value === value;
        const bucket = STATUS_VARIANTS[option.value] ?? 'secondary';
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onValueChange(option.value)}
            className={cn(
              'flex h-11 items-center gap-2 rounded-[10px] border px-3.5 text-[13px] font-semibold transition-colors',
              active
                ? STATUS_BUCKET_ACTIVE[bucket]
                : 'border-border-strong bg-card text-foreground-2 hover:bg-muted',
            )}
          >
            <span
              className={cn(
                'size-1.5 shrink-0 rounded-full',
                active ? STATUS_BUCKET_DOT[bucket] : 'bg-faint',
              )}
            />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** Card de etapa do wizard: header sobre `bg-raised` + corpo + rodapé, no padrão do mockup. */
export function WizardStepCard({
  icon,
  title,
  subtitle,
  stepLabel,
  children,
  footer,
}: {
  icon: IconName;
  title: string;
  subtitle: string;
  stepLabel: string;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[18px] border border-border bg-card">
      <div className="flex items-center justify-between gap-4 border-b border-border bg-raised px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-primary-soft text-primary">
            <Icon name={icon} size={16} />
          </span>
          <div>
            <h2 className="font-display text-[15.5px] font-extrabold tracking-[-0.01em]">
              {title}
            </h2>
            <p className="text-[12px] text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.1em] text-faint">
          {stepLabel}
        </span>
      </div>

      <div className="px-6 py-6">{children}</div>

      <div className="flex items-center justify-between gap-3 border-t border-border bg-raised px-6 py-4">
        {footer}
      </div>
    </div>
  );
}
